import type {
  BotConversationHistory,
  BotKnowledge,
  BotKnowledgeGap,
  BotPreviewReply,
  BotUsage,
  Channel,
  Conversation,
  ConversationMessage,
  Customer,
  DashboardSummary,
  Lead,
  LeadActivity,
  SupportRequest,
  Ticket,
} from '@/types/engage';

/**
 * Foundation-mode fixtures. Used when `VITE_USE_MOCK_DATA` is `1` (default in
 * dev until zafroi-mvp-runtime exposes the real endpoints). Every field shape
 * is the contract the BE must mirror — see docs/BACKEND_CONTRACT.md.
 */

export const fixtureLeads: Lead[] = [
  {
    id: 'lead_001',
    name: 'Anika Sharma',
    company: 'Orchid Interiors',
    phone: '+91 98200 11234',
    email: 'anika@orchid.example',
    source: 'whatsapp',
    status: 'qualified',
    ownerName: 'Rhea Menon',
    lastActivityAt: '2026-05-19T08:42:00Z',
    nextFollowUpAt: '2026-05-21T05:30:00Z',
    tags: ['premium', 'demo-requested'],
    createdAt: '2026-05-12T10:00:00Z',
  },
  {
    id: 'lead_002',
    name: 'Vikram Iyer',
    company: 'Coastal Spice Co.',
    phone: '+91 98765 43210',
    source: 'portal',
    status: 'new',
    lastActivityAt: '2026-05-20T03:11:00Z',
    createdAt: '2026-05-20T03:11:00Z',
  },
  {
    id: 'lead_003',
    name: 'Saira Khan',
    company: 'Nimbus Travel',
    phone: '+91 99710 11199',
    email: 'saira@nimbus.example',
    source: 'referral',
    status: 'follow_up',
    ownerName: 'Aakash Verma',
    lastActivityAt: '2026-05-18T13:25:00Z',
    nextFollowUpAt: '2026-05-22T07:00:00Z',
    createdAt: '2026-05-08T09:15:00Z',
  },
  {
    id: 'lead_004',
    name: 'Devraj Patel',
    company: 'Sunlit Solar',
    phone: '+91 91234 56780',
    email: 'devraj@sunlit.example',
    source: 'campaign',
    status: 'contacted',
    ownerName: 'Rhea Menon',
    lastActivityAt: '2026-05-19T15:48:00Z',
    createdAt: '2026-05-15T11:40:00Z',
  },
  {
    id: 'lead_005',
    name: 'Meher Joshi',
    company: 'Lotus Wellness',
    source: 'website',
    status: 'converted',
    ownerName: 'Aakash Verma',
    lastActivityAt: '2026-05-17T07:00:00Z',
    createdAt: '2026-04-30T08:00:00Z',
  },
  {
    id: 'lead_006',
    name: 'Karan Bedi',
    company: 'Vertex Realty',
    source: 'manual',
    status: 'lost',
    lastActivityAt: '2026-05-10T06:00:00Z',
    createdAt: '2026-04-20T08:00:00Z',
  },
];

export const fixtureLeadActivity: Record<string, LeadActivity[]> = {
  lead_001: [
    {
      id: 'la_011',
      leadId: 'lead_001',
      kind: 'status_change',
      title: 'Marked as Qualified',
      actorName: 'Rhea Menon',
      at: '2026-05-19T08:42:00Z',
    },
    {
      id: 'la_012',
      leadId: 'lead_001',
      kind: 'whatsapp',
      title: 'Sent product brochure',
      body: 'Shared the 12-page premium catalogue via WhatsApp.',
      actorName: 'Rhea Menon',
      at: '2026-05-19T08:30:00Z',
    },
    {
      id: 'la_013',
      leadId: 'lead_001',
      kind: 'call',
      title: 'Discovery call — 22 min',
      body: 'Anika confirmed budget and timeline (Q3). Wants a live demo next week.',
      actorName: 'Rhea Menon',
      at: '2026-05-17T11:05:00Z',
    },
    {
      id: 'la_014',
      leadId: 'lead_001',
      kind: 'note',
      title: 'Initial inbound from WhatsApp campaign',
      actorName: 'System',
      at: '2026-05-12T10:00:00Z',
    },
  ],
};

export const fixtureCustomers: Customer[] = [
  {
    id: 'cus_001',
    name: 'Meher Joshi',
    company: 'Lotus Wellness',
    phone: '+91 98300 22119',
    email: 'meher@lotus.example',
    lifecycle: 'active',
    primaryChannel: 'whatsapp',
    totalConversations: 14,
    openSupportRequests: 1,
    lastInteractionAt: '2026-05-20T05:12:00Z',
    joinedAt: '2025-11-10T09:00:00Z',
  },
  {
    id: 'cus_002',
    name: 'Aarav Banerjee',
    company: 'Banyan Foods',
    phone: '+91 90400 67100',
    email: 'aarav@banyan.example',
    lifecycle: 'active',
    primaryChannel: 'portal',
    totalConversations: 22,
    openSupportRequests: 0,
    lastInteractionAt: '2026-05-19T16:40:00Z',
    joinedAt: '2025-08-04T10:00:00Z',
  },
  {
    id: 'cus_003',
    name: 'Reema Kapoor',
    company: 'Kapoor & Sons',
    phone: '+91 98233 11099',
    lifecycle: 'dormant',
    primaryChannel: 'email',
    totalConversations: 5,
    openSupportRequests: 0,
    lastInteractionAt: '2026-03-02T11:00:00Z',
    joinedAt: '2024-12-15T11:00:00Z',
  },
  {
    id: 'cus_004',
    name: 'Sahil Mehra',
    company: 'Mehra Textiles',
    phone: '+91 90100 44778',
    lifecycle: 'active',
    primaryChannel: 'whatsapp',
    totalConversations: 8,
    openSupportRequests: 2,
    lastInteractionAt: '2026-05-20T02:25:00Z',
    joinedAt: '2026-01-22T10:00:00Z',
  },
  {
    id: 'cus_005',
    name: 'Hariraj Singh',
    company: 'Hariraj Singh & Sons',
    phone: '+91 98110 23456',
    email: 'hariraj@hssons.example',
    lifecycle: 'active',
    primaryChannel: 'whatsapp',
    totalConversations: 3,
    openSupportRequests: 1,
    lastInteractionAt: '2026-05-20T04:40:00Z',
    joinedAt: '2025-09-18T10:00:00Z',
  },
];

export const fixtureConversations: Conversation[] = [
  {
    id: 'conv_001',
    customerId: 'cus_001',
    customerName: 'Meher Joshi',
    channel: 'whatsapp',
    status: 'awaiting_agent',
    subject: 'Reorder request — May batch',
    preview: 'Hi! Can we lock in the same SKUs as April? Need delivery by 27th.',
    unread: true,
    assigneeName: 'Rhea Menon',
    lastMessageAt: '2026-05-20T05:12:00Z',
    messageCount: 6,
  },
  {
    id: 'conv_002',
    customerId: 'cus_002',
    customerName: 'Aarav Banerjee',
    channel: 'portal',
    status: 'open',
    subject: 'Q2 contract review',
    preview: 'Sharing the updated MSA — please review section 4.2 (renewal).',
    unread: false,
    assigneeName: 'Aakash Verma',
    lastMessageAt: '2026-05-19T16:40:00Z',
    messageCount: 14,
  },
  {
    id: 'conv_003',
    customerId: 'cus_004',
    customerName: 'Sahil Mehra',
    channel: 'whatsapp',
    status: 'awaiting_customer',
    preview: "We've shipped the replacement consignment via Blue Dart — tracking shared.",
    unread: false,
    assigneeName: 'Rhea Menon',
    lastMessageAt: '2026-05-20T02:25:00Z',
    messageCount: 22,
  },
  {
    id: 'conv_004',
    customerId: 'cus_003',
    customerName: 'Reema Kapoor',
    channel: 'email',
    status: 'resolved',
    subject: 'Festive catalogue request',
    preview: "Thanks for the catalogue — we'll revisit closer to the launch window.",
    unread: false,
    lastMessageAt: '2026-04-28T09:00:00Z',
    messageCount: 4,
  },
  {
    id: 'conv_005',
    customerId: 'cus_005',
    customerName: 'Hariraj Singh',
    channel: 'whatsapp',
    status: 'open',
    subject: 'Bulk order for the festive quarter',
    preview: 'Planning a larger order this quarter — can we discuss volume pricing?',
    unread: true,
    assigneeName: 'Aakash Verma',
    lastMessageAt: '2026-05-20T04:40:00Z',
    messageCount: 9,
  },
  {
    id: 'conv_006',
    customerId: 'cus_005',
    customerName: 'Hariraj Singh',
    channel: 'portal',
    status: 'awaiting_agent',
    subject: 'Delivery schedule for SO-3104',
    preview: 'Need the dispatch date for my in-transit order — customer is asking.',
    unread: false,
    assigneeName: 'Rhea Menon',
    lastMessageAt: '2026-05-19T12:10:00Z',
    messageCount: 5,
  },
  {
    id: 'conv_007',
    customerId: 'cus_005',
    customerName: 'Hariraj Singh',
    channel: 'phone',
    status: 'resolved',
    subject: 'Payment terms clarification',
    preview: 'Confirmed 30-day terms for repeat orders. Closed.',
    unread: false,
    lastMessageAt: '2026-05-14T07:30:00Z',
    messageCount: 3,
  },
];

export const fixtureConversationMessages: Record<string, ConversationMessage[]> = {
  conv_001: [
    {
      id: 'm_011',
      conversationId: 'conv_001',
      direction: 'inbound',
      authorName: 'Meher Joshi',
      body: 'Hi! Can we lock in the same SKUs as April? Need delivery by 27th.',
      at: '2026-05-20T05:12:00Z',
      channel: 'whatsapp',
    },
    {
      id: 'm_010',
      conversationId: 'conv_001',
      direction: 'internal',
      authorName: 'Rhea Menon',
      body: 'Looping in ops — confirming stock on the wellness SKUs.',
      at: '2026-05-20T05:14:00Z',
    },
    {
      id: 'm_009',
      conversationId: 'conv_001',
      direction: 'outbound',
      authorName: 'Rhea Menon',
      body: "Got it Meher — checking stock now and I'll confirm within the hour.",
      at: '2026-05-20T05:15:00Z',
      channel: 'whatsapp',
    },
  ],
};

export const fixtureSupportRequests: SupportRequest[] = [
  {
    id: 'sr_001',
    customerId: 'cus_001',
    customerName: 'Meher Joshi',
    subject: 'Damaged carton on May 14 shipment',
    description:
      'Two cartons of the wellness SKUs arrived damaged. Photos attached on WhatsApp.',
    priority: 'high',
    status: 'pending',
    channel: 'whatsapp',
    createdAt: '2026-05-20T03:42:00Z',
    updatedAt: '2026-05-20T03:42:00Z',
  },
  {
    id: 'sr_002',
    customerId: 'cus_004',
    customerName: 'Sahil Mehra',
    subject: 'Invoice mismatch on order MX-2241',
    priority: 'medium',
    status: 'triaged',
    channel: 'portal',
    createdAt: '2026-05-19T11:11:00Z',
    updatedAt: '2026-05-19T15:00:00Z',
    linkedTicketId: 'tkt_002',
  },
  {
    id: 'sr_003',
    customerId: 'cus_002',
    customerName: 'Aarav Banerjee',
    subject: 'Need GST certificate copy',
    priority: 'low',
    status: 'in_progress',
    channel: 'email',
    createdAt: '2026-05-18T09:00:00Z',
    updatedAt: '2026-05-19T10:00:00Z',
  },
  {
    id: 'sr_004',
    customerId: 'cus_004',
    customerName: 'Sahil Mehra',
    subject: 'Replacement consignment tracking',
    priority: 'urgent',
    status: 'in_progress',
    channel: 'whatsapp',
    createdAt: '2026-05-19T07:30:00Z',
    updatedAt: '2026-05-20T02:25:00Z',
    linkedTicketId: 'tkt_001',
  },
  {
    id: 'sr_005',
    customerId: 'cus_005',
    customerName: 'Hariraj Singh',
    subject: 'Dispatch delay on order SO-3104',
    description:
      'In-transit order SO-3104 has not moved in 48h. Customer wants an updated ETA.',
    priority: 'high',
    status: 'in_progress',
    channel: 'portal',
    createdAt: '2026-05-19T12:10:00Z',
    updatedAt: '2026-05-20T04:40:00Z',
  },
  {
    id: 'sr_006',
    customerId: 'cus_002',
    customerName: 'Aarav Banerjee',
    subject: 'Password reset confirmation',
    priority: 'low',
    status: 'resolved',
    channel: 'email',
    createdAt: '2026-05-17T06:00:00Z',
    updatedAt: '2026-05-17T09:35:00Z',
    resolvedAt: '2026-05-17T09:35:00Z',
    resolutionNotes: 'Sent MFA reset link; customer confirmed access restored.',
  },
  {
    id: 'sr_007',
    customerId: 'cus_001',
    customerName: 'Meher Joshi',
    subject: 'Duplicate ticket — already handled via call',
    priority: 'low',
    status: 'closed',
    channel: 'portal',
    createdAt: '2026-05-15T04:20:00Z',
    updatedAt: '2026-05-15T11:00:00Z',
  },
];

export const fixtureTickets: Ticket[] = [
  {
    id: 'tkt_001',
    reference: 'ENG-1024',
    subject: 'Replacement consignment for MX-2210',
    customerName: 'Sahil Mehra',
    customerId: 'cus_004',
    status: 'escalated',
    priority: 'urgent',
    assigneeName: 'Aakash Verma',
    escalated: true,
    slaDueAt: '2026-05-20T18:00:00Z',
    createdAt: '2026-05-19T07:30:00Z',
    updatedAt: '2026-05-20T02:25:00Z',
    description:
      'Customer reported missing 3 of 5 cartons in the latest dispatch. Replacement consignment in transit; awaiting courier confirmation.',
  },
  {
    id: 'tkt_002',
    reference: 'ENG-1023',
    subject: 'Invoice mismatch — order MX-2241',
    customerName: 'Sahil Mehra',
    customerId: 'cus_004',
    status: 'in_progress',
    priority: 'medium',
    assigneeName: 'Rhea Menon',
    escalated: false,
    slaDueAt: '2026-05-22T12:00:00Z',
    createdAt: '2026-05-19T11:11:00Z',
    updatedAt: '2026-05-19T15:00:00Z',
  },
  {
    id: 'tkt_003',
    reference: 'ENG-1021',
    subject: 'Portal login MFA reset',
    customerName: 'Aarav Banerjee',
    customerId: 'cus_002',
    status: 'resolved',
    priority: 'low',
    assigneeName: 'Aakash Verma',
    escalated: false,
    createdAt: '2026-05-16T08:00:00Z',
    updatedAt: '2026-05-17T09:30:00Z',
  },
];

/**
 * Default email auto-respond template — matches the seed the runtime writes
 * when a tenant creates an EMAIL channel without supplying their own. Kept
 * in sync with `docs/CHANNELS.md` §5 and the backend's default constants.
 */
export const DEFAULT_EMAIL_TEMPLATE_SUBJECT =
  'Re: {{originalSubject}} [Ticket #{{ticketNumber}}]';

export const DEFAULT_EMAIL_TEMPLATE_BODY = [
  'Hi {{customerName}},',
  '',
  "Thanks for reaching out to {{tenantName}}. We've received your message and a member of our team will get back to you shortly.",
  '',
  'For reference, your ticket number is {{ticketNumber}} — please keep it in the subject line if you reply, so we can keep your conversation together.',
  '',
  'Talk soon,',
  'The {{tenantName}} team',
].join('\n');

export const fixtureChannels: Channel[] = [
  {
    id: 'ch_mavk_wa',
    kind: 'whatsapp',
    label: 'WhatsApp Business',
    status: 'connected',
    identifier: '+91 98220 11000',
    lastSyncedAt: '2026-05-22T06:00:00Z',
    templateSubject: null,
    templateBody: null,
    config: { provider: 'meta_cloud_api' },
    createdAt: '2026-03-04T10:00:00Z',
    updatedAt: '2026-05-22T06:00:00Z',
  },
  {
    id: 'ch_mavk_portal',
    kind: 'portal',
    label: 'Customer Portal',
    status: 'connected',
    identifier: 'mavk.zafroi.com',
    lastSyncedAt: '2026-05-22T06:00:00Z',
    templateSubject: null,
    templateBody: null,
    config: null,
    createdAt: '2026-03-04T10:00:00Z',
    updatedAt: '2026-05-22T06:00:00Z',
  },
  {
    id: 'ch_mavk_email',
    kind: 'email',
    label: 'Support inbox',
    status: 'disconnected',
    identifier: 'support@mavk.example',
    lastSyncedAt: null,
    templateSubject: DEFAULT_EMAIL_TEMPLATE_SUBJECT,
    templateBody: DEFAULT_EMAIL_TEMPLATE_BODY,
    config: null,
    createdAt: '2026-05-19T09:00:00Z',
    updatedAt: '2026-05-19T09:00:00Z',
  },
  {
    id: 'ch_mavk_sms',
    kind: 'sms',
    label: 'Transactional SMS',
    status: 'disconnected',
    identifier: null,
    lastSyncedAt: null,
    templateSubject: null,
    templateBody: null,
    config: null,
    createdAt: '2026-05-04T10:00:00Z',
    updatedAt: '2026-05-04T10:00:00Z',
  },
  {
    id: 'ch_mavk_webchat',
    kind: 'webchat',
    label: 'Site widget',
    status: 'connected',
    identifier: 'mavk.example',
    lastSyncedAt: '2026-05-22T06:00:00Z',
    templateSubject: null,
    templateBody: null,
    config: null,
    createdAt: '2026-03-04T10:00:00Z',
    updatedAt: '2026-05-22T06:00:00Z',
  },
];

export const fixtureDashboardSummary: DashboardSummary = {
  newLeads: { value: 12, delta: 4 },
  openConversations: { value: 34, delta: -3 },
  pendingSupport: { value: 8, delta: 2 },
  escalatedTickets: { value: 2, delta: 1 },
  pendingFollowUps: { value: 17, delta: 0 },
  engagementToday: { value: 184, delta: 22 },
};

/**
 * Simulated ZAFROI Operations (ERP) sales-order data — the slice the runtime
 * federates into the assistant. Keyed by the ecosystem-wide customer id.
 * Engage does NOT own orders; this fixture exists only so foundation/mock
 * mode can demonstrate the cross-app copilot. Real data comes from the
 * runtime federating the ERP modules Engage is granted access to.
 */
export interface MockErpOrder {
  reference: string;
  status: 'fulfilled' | 'in_transit' | 'processing' | 'cancelled';
  total: number;
  placedAt: string;
}

export const fixtureCustomerOrders: Record<string, MockErpOrder[]> = {
  cus_001: [
    { reference: 'SO-2890', status: 'fulfilled', total: 72000, placedAt: '2025-12-20' },
    { reference: 'SO-2912', status: 'fulfilled', total: 64000, placedAt: '2026-01-28' },
    { reference: 'SO-2934', status: 'fulfilled', total: 58000, placedAt: '2026-03-05' },
    { reference: 'SO-2951', status: 'in_transit', total: 61000, placedAt: '2026-04-18' },
    { reference: 'SO-2970', status: 'fulfilled', total: 55000, placedAt: '2026-05-10' },
  ],
  cus_004: [
    { reference: 'SO-2980', status: 'fulfilled', total: 64000, placedAt: '2026-02-10' },
    { reference: 'SO-3001', status: 'fulfilled', total: 52000, placedAt: '2026-03-15' },
    { reference: 'SO-3015', status: 'in_transit', total: 48000, placedAt: '2026-04-22' },
    { reference: 'SO-3033', status: 'processing', total: 46000, placedAt: '2026-05-12' },
  ],
  cus_005: [
    { reference: 'SO-3041', status: 'fulfilled', total: 92000, placedAt: '2025-10-12' },
    { reference: 'SO-3052', status: 'fulfilled', total: 68000, placedAt: '2025-12-03' },
    { reference: 'SO-3068', status: 'fulfilled', total: 54000, placedAt: '2026-01-20' },
    { reference: 'SO-3079', status: 'fulfilled', total: 76000, placedAt: '2026-02-28' },
    { reference: 'SO-3090', status: 'fulfilled', total: 41000, placedAt: '2026-03-25' },
    { reference: 'SO-3104', status: 'in_transit', total: 58000, placedAt: '2026-05-08' },
    { reference: 'SO-3119', status: 'processing', total: 31000, placedAt: '2026-05-18' },
  ],
};

const ORDER_STATUS_WORD: Record<MockErpOrder['status'], string> = {
  fulfilled: 'fulfilled',
  in_transit: 'in transit',
  processing: 'processing',
  cancelled: 'cancelled',
};

function formatInr(amount: number): string {
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
  return `₹${amount.toLocaleString('en-IN')}`;
}

function findCustomerInPrompt(text: string): Customer | undefined {
  return fixtureCustomers.find((c) => text.includes(c.name.toLowerCase()));
}

/**
 * Builds a cross-app customer view by joining ERP orders with Engage
 * conversations/support/tickets — the answer shape the real runtime
 * federation produces.
 */
function federatedCustomerAnswer(
  customer: Customer,
  ordersFirst: boolean,
): string {
  const orders = fixtureCustomerOrders[customer.id] ?? [];
  const orderTotal = orders.reduce((sum, o) => sum + o.total, 0);
  const byStatus = orders.reduce<Record<string, number>>((acc, o) => {
    acc[o.status] = (acc[o.status] ?? 0) + 1;
    return acc;
  }, {});
  const recentOrder = [...orders].sort((a, b) =>
    b.placedAt.localeCompare(a.placedAt),
  )[0];

  const convs = fixtureConversations.filter(
    (c) => c.customerId === customer.id,
  );
  const openConvs = convs.filter(
    (c) => c.status !== 'resolved' && c.status !== 'archived',
  );
  const support = fixtureSupportRequests.filter(
    (s) => s.customerId === customer.id,
  );
  const tickets = fixtureTickets.filter((t) => t.customerId === customer.id);

  const ordersBlock =
    orders.length === 0
      ? ['Sales orders (ZAFROI Operations): none on record.']
      : [
          `Sales orders (ZAFROI Operations): ${orders.length} orders · ${formatInr(orderTotal)} total`,
          `  • ${Object.entries(byStatus)
            .map(([s, n]) => `${n} ${ORDER_STATUS_WORD[s as MockErpOrder['status']]}`)
            .join(', ')}`,
          `  • Most recent: ${recentOrder.reference} (${formatInr(recentOrder.total)}) · ${ORDER_STATUS_WORD[recentOrder.status]}`,
        ];

  const engageBlock = [
    `Engage activity: ${convs.length} conversation${convs.length === 1 ? '' : 's'} (${openConvs.length} open) · ${support.length} support request${support.length === 1 ? '' : 's'} · ${tickets.length} ticket${tickets.length === 1 ? '' : 's'}`,
    ...support
      .filter((s) => s.status !== 'closed' && s.status !== 'resolved')
      .map((s) => `  • Open: "${s.subject}" — ${s.priority} priority`),
  ];

  const head = `${customer.name}${customer.company ? ` · ${customer.company}` : ''} — federated view (Operations + Engage)`;
  const body = ordersFirst
    ? [...ordersBlock, '', ...engageBlock]
    : [...engageBlock, '', ...ordersBlock];

  return [
    head,
    '',
    ...body,
    '',
    'Sources: ZAFROI Operations + Engage, federated by the runtime.',
  ].join('\n');
}

type EngageEntity =
  | 'customers'
  | 'leads'
  | 'conversations'
  | 'support'
  | 'tickets'
  | 'channels';

function detectEntity(text: string): EngageEntity | null {
  if (/customer|client|account/.test(text)) return 'customers';
  if (/lead|prospect/.test(text)) return 'leads';
  if (/conversation|inbox|thread/.test(text)) return 'conversations';
  if (/support/.test(text)) return 'support';
  if (/ticket/.test(text)) return 'tickets';
  if (/channel/.test(text)) return 'channels';
  return null;
}

function countBy<T>(
  items: T[],
  key: (item: T) => string,
): Record<string, number> {
  return items.reduce<Record<string, number>>((acc, item) => {
    const k = key(item);
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});
}

function breakdown(counts: Record<string, number>): string {
  return Object.entries(counts)
    .map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`)
    .join(', ');
}

/**
 * Count + one-line breakdown for a core Engage entity — covers the common
 * "how many X do I have / list X" questions.
 */
function entityCountAnswer(entity: EngageEntity): string {
  switch (entity) {
    case 'customers':
      return [
        `You have ${fixtureCustomers.length} customers in this Engage workspace.`,
        `  • ${breakdown(countBy(fixtureCustomers, (c) => c.lifecycle))}`,
        'Ask for a 360 on any of them — e.g. "give me a 360 view of Hariraj Singh".',
      ].join('\n');
    case 'leads':
      return [
        `You have ${fixtureLeads.length} leads in the pipeline.`,
        `  • ${breakdown(countBy(fixtureLeads, (l) => l.status))}`,
        'Ask "which leads need follow-up?" to see what is due.',
      ].join('\n');
    case 'conversations': {
      const open = fixtureConversations.filter(
        (c) => c.status !== 'resolved' && c.status !== 'archived',
      ).length;
      return [
        `You have ${fixtureConversations.length} conversations — ${open} still open.`,
        `  • by channel: ${breakdown(countBy(fixtureConversations, (c) => c.channel))}`,
      ].join('\n');
    }
    case 'support':
      return [
        `You have ${fixtureSupportRequests.length} support requests.`,
        `  • ${breakdown(countBy(fixtureSupportRequests, (s) => s.status))}`,
      ].join('\n');
    case 'tickets': {
      const escalated = fixtureTickets.filter((t) => t.escalated).length;
      return [
        `You have ${fixtureTickets.length} tickets — ${escalated} escalated.`,
        `  • ${breakdown(countBy(fixtureTickets, (t) => t.status))}`,
      ].join('\n');
    }
    case 'channels': {
      const connected = fixtureChannels.filter(
        (c) => c.status === 'connected',
      ).length;
      return [
        `You have ${fixtureChannels.length} channels — ${connected} connected.`,
        `  • ${fixtureChannels.map((c) => `${c.label} (${c.status})`).join(', ')}`,
      ].join('\n');
    }
  }
}

/**
 * Foundation-mode reply generator for the internal assistant. Pattern-matches
 * the prompt to a plausible answer drawn from the fixtures above — including
 * cross-app (ERP + Engage) customer questions and entity counts. The real
 * runtime endpoint replaces this with live federation — see
 * docs/BACKEND_CONTRACT.md.
 */
export function mockAssistantReply(message: string): string {
  const text = message.toLowerCase();
  const customer = findCustomerInPrompt(text);

  if (customer) {
    if (/draft|reply|respond|write/.test(text)) {
      return [
        `Draft for ${customer.name}:`,
        '',
        `"Hi ${customer.name.split(' ')[0]}, thanks for your patience. We've looked into this and have an update for you — {{update}}. We'll keep you posted at every step. Anything else I can help with?"`,
        '',
        "Tell me the situation and I'll tailor it further.",
      ].join('\n');
    }
    return federatedCustomerAnswer(customer, /order|sales|purchas/.test(text));
  }

  if (/order|sales|purchas/.test(text)) {
    return [
      'Sales orders live in ZAFROI Operations — I can federate them into a customer view here.',
      'Tell me which customer (e.g. "how many sales orders from Hariraj Singh?") and I\'ll pull Operations + Engage together.',
    ].join('\n');
  }

  if (/follow.?up/.test(text)) {
    const dueLeads = fixtureLeads.filter(
      (l) => l.status === 'follow_up' || Boolean(l.nextFollowUpAt),
    );
    return [
      `You have ${fixtureDashboardSummary.pendingFollowUps.value} follow-ups pending across the workspace.`,
      'Nearest from the lead pipeline:',
      ...dueLeads.slice(0, 3).map((l) => `• ${l.name}${l.company ? ` (${l.company})` : ''} — owner ${l.ownerName ?? 'unassigned'}`),
      'Want me to draft a check-in message for any of them?',
    ].join('\n');
  }

  if (/escalat|sla/.test(text)) {
    const escalated = fixtureTickets.filter((t) => t.escalated);
    return [
      `${escalated.length} ticket${escalated.length === 1 ? '' : 's'} currently escalated:`,
      ...escalated.map((t) => `• ${t.reference} — ${t.subject} (${t.customerName})`),
      'These are crossing their SLA window. The owner should be looped in now.',
    ].join('\n');
  }

  if (/draft|reply|respond|message|write/.test(text)) {
    return [
      "Here's a draft you can adapt:",
      '',
      '"Hi {{name}}, thanks for your patience. We\'ve looked into this and have an update for you — {{update}}. We\'ll keep you posted at every step. Anything else I can help with?"',
      '',
      'Tell me the customer and the situation and I\'ll tailor it.',
    ].join('\n');
  }

  const entity = detectEntity(text);
  if (entity) return entityCountAnswer(entity);

  if (/summary|today|overview|pulse|how are we/.test(text)) {
    const s = fixtureDashboardSummary;
    return [
      "Today's engagement snapshot:",
      `• ${s.newLeads.value} new leads · ${s.openConversations.value} open conversations`,
      `• ${s.pendingSupport.value} support requests pending · ${s.escalatedTickets.value} tickets escalated`,
      `• ${s.pendingFollowUps.value} follow-ups due · ${s.engagementToday.value} touchpoints logged`,
      'The escalated tickets are the most time-sensitive — start there.',
    ].join('\n');
  }

  return [
    "I'm the Engage Assistant — your ecosystem copilot across ZAFROI Operations and Engage.",
    'I can count your customers, leads, conversations, support requests and tickets, pull a customer 360 (sales orders + conversations + support), summarize engagement, flag escalations, and draft replies.',
    'Try "how many customers do I have?", "how many sales orders from Hariraj Singh?", or "what needs my attention today?".',
  ].join('\n');
}

// ─── Bot API (customer-facing chatbot) ──────────────────────────────────────

/**
 * Knowledge-base entries for the constrained customer-facing bot. A mix of
 * `approved` (the bot may answer from these) and `draft` (pending tenant
 * review — invisible to the bot until approved). Real entries come from the
 * runtime's `engage/bot-api` module — see docs/BACKEND_CONTRACT.md.
 */
export const fixtureBotKnowledge: BotKnowledge[] = [
  {
    id: 'bk_001',
    title: 'Business hours',
    content:
      'Our team is available Monday to Saturday, 9:00 AM to 7:00 PM IST. We are closed on Sundays and national holidays. Messages sent outside these hours are answered the next working day.',
    category: 'General',
    status: 'approved',
    approvedBy: 'Rhea Menon',
    approvedAt: '2026-05-10T09:00:00Z',
    createdAt: '2026-05-08T10:00:00Z',
    updatedAt: '2026-05-10T09:00:00Z',
  },
  {
    id: 'bk_002',
    title: 'Delivery timelines',
    content:
      'Standard orders are dispatched within 2 business days and delivered in 4–6 business days. You will receive a tracking link by WhatsApp once your order ships. Bulk orders may take longer — our team will confirm a dispatch date.',
    category: 'Orders & shipping',
    status: 'approved',
    approvedBy: 'Rhea Menon',
    approvedAt: '2026-05-11T11:30:00Z',
    createdAt: '2026-05-09T08:00:00Z',
    updatedAt: '2026-05-11T11:30:00Z',
  },
  {
    id: 'bk_003',
    title: 'Returns and refunds',
    content:
      'Unused items in original packaging can be returned within 7 days of delivery. Refunds are processed to the original payment method within 5–7 business days after we receive the returned item. Damaged or incorrect items are replaced free of charge.',
    category: 'Orders & shipping',
    status: 'approved',
    approvedBy: 'Aakash Verma',
    approvedAt: '2026-05-12T14:00:00Z',
    createdAt: '2026-05-09T08:30:00Z',
    updatedAt: '2026-05-12T14:00:00Z',
  },
  {
    id: 'bk_004',
    title: 'Payment methods',
    content:
      'We accept UPI, all major credit and debit cards, net banking, and bank transfer. For repeat business customers, 30-day credit terms are available on request. We do not accept cash on delivery.',
    category: 'Billing',
    status: 'approved',
    approvedBy: 'Aakash Verma',
    approvedAt: '2026-05-13T10:15:00Z',
    createdAt: '2026-05-10T09:45:00Z',
    updatedAt: '2026-05-13T10:15:00Z',
  },
  {
    id: 'bk_005',
    title: 'How to track my order',
    content:
      'You can track your order using the tracking link sent to you on WhatsApp when it ships. If you have not received a link, share your order reference (e.g. SO-3104) and our team will look it up.',
    category: 'Orders & shipping',
    status: 'draft',
    createdAt: '2026-05-18T12:00:00Z',
    updatedAt: '2026-05-18T12:00:00Z',
  },
  {
    id: 'bk_006',
    title: 'Bulk and wholesale pricing',
    content:
      'Volume pricing is available for orders above 50 units. Discounts are tiered by quantity. Share your expected order size and we will send a custom quote within one business day.',
    category: 'Billing',
    status: 'draft',
    createdAt: '2026-05-19T09:30:00Z',
    updatedAt: '2026-05-19T09:30:00Z',
  },
];

/**
 * Knowledge gaps for the self-improving knowledge base — clusters of customer
 * questions the constrained bot could NOT answer (no approved KB match), so
 * the tenant can see what is missing and write the article. The real runtime
 * produces these via an LLM clustering pass over unanswered questions; this
 * fixture stands in for foundation/mock mode — see docs/BACKEND_CONTRACT.md.
 */
export const mockBotGaps: BotKnowledgeGap[] = [
  {
    topic: 'Gift wrapping',
    count: 5,
    examples: [
      'Do you offer gift wrapping?',
      'Is gift wrapping available?',
      'Can my order be wrapped as a gift?',
    ],
  },
  {
    topic: 'International shipping',
    count: 4,
    examples: [
      'Do you ship internationally?',
      'Can you deliver outside India?',
      'What are the shipping charges to the UAE?',
    ],
  },
  {
    topic: 'Changing a delivery address',
    count: 3,
    examples: [
      'Can I change the delivery address on my order?',
      'I entered the wrong address — how do I fix it?',
    ],
  },
];

/** Stop-words ignored when keyword-matching a customer message. */
const BOT_STOP_WORDS = new Set([
  'the', 'a', 'an', 'is', 'are', 'do', 'does', 'can', 'i', 'my', 'me', 'you',
  'your', 'we', 'how', 'what', 'when', 'where', 'to', 'of', 'for', 'and', 'or',
  'on', 'in', 'with', 'about', 'please', 'hi', 'hello', 'hey', 'help', 'need',
  'want', 'get', 'have', 'this', 'that', 'it', 'be', 'will', 'would',
]);

/** Words that signal the customer is reporting an issue / asking to raise one. */
const BOT_COMPLAINT_WORDS = [
  'complaint',
  'raise',
  'damaged',
  'broken',
  'problem',
  'missing',
  'not arrived',
  'wrong',
];

/** Builds a fake raised support request — the shape the runtime returns. */
function mockRaisedRequest(subject: string): BotPreviewReply['requestRaised'] {
  const ref = `SR-${Math.floor(100000 + Math.random() * 900000)}`;
  return {
    id: `sr_${Math.random().toString(36).slice(2, 8)}`,
    reference: ref,
    subject,
  };
}

/**
 * Foundation-mode reply generator for the constrained customer-facing bot.
 * Mimics the runtime's `engage/bot-api` behaviour: it keyword-matches the
 * customer message ONLY against APPROVED knowledge entries. If it finds a
 * match it returns a grounded reply citing those entries; if nothing matches
 * it hands off to a human. When the message looks like an issue report — or
 * is an in-scope question it cannot answer — it also raises a support request
 * on the customer's behalf. The real runtime endpoint replaces this with a
 * retrieval-grounded model — see docs/BACKEND_CONTRACT.md.
 */
export function mockBotPreviewReply(
  message: string,
  _customerId?: string,
): BotPreviewReply {
  const text = message.toLowerCase();
  const tokens = text
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 2 && !BOT_STOP_WORDS.has(t));

  const approved = fixtureBotKnowledge.filter((k) => k.status === 'approved');

  const scored = approved
    .map((entry) => {
      const haystack = `${entry.title} ${entry.content} ${entry.category ?? ''}`
        .toLowerCase();
      const hits = tokens.filter((t) => haystack.includes(t)).length;
      return { entry, hits };
    })
    .filter((s) => s.hits > 0)
    .sort((a, b) => b.hits - a.hits)
    .slice(0, 3);

  const isComplaint = BOT_COMPLAINT_WORDS.some((w) => text.includes(w));

  // Issue report → confirm the request was raised, grounded.
  if (isComplaint) {
    const subject = `Issue reported: ${message.trim().slice(0, 60)}`;
    return {
      reply:
        "Thanks for letting us know — I've raised a support request and our team will follow up with you shortly.",
      grounded: true,
      handoff: false,
      sources: scored.map(({ entry }) => ({ id: entry.id, title: entry.title })),
      requestRaised: mockRaisedRequest(subject),
      suggestions: ['Ask something else'],
    };
  }

  // In-scope question with no approved-KB match → escalate AND raise a request.
  if (scored.length === 0) {
    return {
      reply:
        "I'm not able to answer that just yet — I've raised a request so a team member can help you directly.",
      grounded: false,
      handoff: true,
      sources: [],
      requestRaised: mockRaisedRequest(
        `Unanswered question: ${message.trim().slice(0, 60)}`,
      ),
      suggestions: ['Ask something else'],
    };
  }

  const top = scored[0].entry;
  return {
    reply: `${top.content}\n\nIs there anything else I can help you with?`,
    grounded: true,
    handoff: false,
    sources: scored.map(({ entry }) => ({ id: entry.id, title: entry.title })),
    requestRaised: null,
    suggestions: ['Raise a request', 'Ask something else'],
  };
}

// ─── Bot conversation history (customer chat widget restore) ───────────────

/**
 * Realistic per-customer history for the customer-facing chat widget. Keyed
 * by `customerId` so the mock can vary by customer — unknown customers fall
 * back to an empty thread (matching the live empty state).
 *
 * Timestamps are anchored ~yesterday so the divider's relative-time label
 * reads naturally (e.g. "1 day ago"). The runtime returns oldest-first.
 */
const yesterday = (hour: number, minute = 0): string => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  d.setUTCHours(hour, minute, 0, 0);
  return d.toISOString();
};

const mockBotConversationByCustomer: Record<string, BotConversationHistory> = {
  // Meher Joshi — chatted yesterday about reorder + delivery window.
  cus_001: {
    conversationId: 'bc_cus_001',
    lastMessageAt: yesterday(11, 14),
    messages: [
      {
        id: 'bcm_cus_001_1',
        role: 'user',
        content: 'Hi, can I reorder the same SKUs as last month?',
        at: yesterday(11, 10),
      },
      {
        id: 'bcm_cus_001_2',
        role: 'assistant',
        content:
          'Yes — I can see your April order on file. Would you like the same quantities, or adjust any items?',
        at: yesterday(11, 11),
      },
      {
        id: 'bcm_cus_001_3',
        role: 'user',
        content: 'Same quantities. Need delivery by the 27th.',
        at: yesterday(11, 13),
      },
      {
        id: 'bcm_cus_001_4',
        role: 'assistant',
        content:
          "Got it — I've passed the reorder along to your account manager and flagged the 27th delivery window.",
        at: yesterday(11, 14),
      },
    ],
  },
  // Aarav Banerjee — asked about portal access yesterday afternoon.
  cus_002: {
    conversationId: 'bc_cus_002',
    lastMessageAt: yesterday(15, 22),
    messages: [
      {
        id: 'bcm_cus_002_1',
        role: 'user',
        content: 'Where do I find the renewal section of the MSA?',
        at: yesterday(15, 20),
      },
      {
        id: 'bcm_cus_002_2',
        role: 'assistant',
        content:
          "It's section 4.2 of the document in your portal. I can share the direct link if that helps.",
        at: yesterday(15, 22),
      },
    ],
  },
  // Hariraj Singh — quick check on order status.
  cus_005: {
    conversationId: 'bc_cus_005',
    lastMessageAt: yesterday(9, 5),
    messages: [
      {
        id: 'bcm_cus_005_1',
        role: 'user',
        content: 'Order status please',
        at: yesterday(9, 0),
      },
      {
        id: 'bcm_cus_005_2',
        role: 'assistant',
        content:
          'Your latest order is out for dispatch and should arrive within 2 business days.',
        at: yesterday(9, 2),
      },
      {
        id: 'bcm_cus_005_3',
        role: 'user',
        content: 'Thanks!',
        at: yesterday(9, 5),
      },
    ],
  },
};

/**
 * Mock for `GET /engage/bot-api/conversation`. Returns the customer's
 * persisted thread, or the empty-state shape for an unknown customer.
 */
export function mockBotConversation(
  customerId: string,
  _limit = 20,
): BotConversationHistory {
  return (
    mockBotConversationByCustomer[customerId] ?? {
      conversationId: null,
      lastMessageAt: null,
      messages: [],
    }
  );
}

// ─── Bot usage (plan + monthly turns) ───────────────────────────────────────

/**
 * Default fixture for `GET /engage/bot-api/usage`. The MVP's first beta
 * customer (MAVK) is on the BETA tier with unlimited turns — this matches
 * what the UI sees today. To exercise the non-unlimited UI in dev, set
 * `VITE_MOCK_BOT_PLAN` (e.g. `GROWTH`) and optionally `VITE_MOCK_BOT_TURNS`.
 */
function buildMockBotUsage(): BotUsage {
  const env = import.meta.env as Record<string, string | undefined>;
  const planEnv = env.VITE_MOCK_BOT_PLAN?.toUpperCase();
  const turnsEnv = env.VITE_MOCK_BOT_TURNS
    ? Number(env.VITE_MOCK_BOT_TURNS)
    : undefined;

  // Helper to pick a status from a usage ratio.
  const statusFor = (pct: number): BotUsage['usage']['status'] => {
    if (pct >= 120) return 'hard_cap';
    if (pct >= 100) return 'over';
    if (pct >= 80) return 'approaching';
    return 'under';
  };

  // Per-tier defaults — kept aligned with the docs in ChatbotGuidePage's
  // pricing section. `null` means unlimited.
  const tierPlans: Record<
    'BETA' | 'STARTER' | 'GROWTH' | 'SCALE' | 'ENTERPRISE',
    { includedBotTurns: number | null; agentSeats: number | null; allowOverage: boolean }
  > = {
    BETA: { includedBotTurns: null, agentSeats: null, allowOverage: false },
    STARTER: { includedBotTurns: 1000, agentSeats: 2, allowOverage: true },
    GROWTH: { includedBotTurns: 5000, agentSeats: 5, allowOverage: true },
    SCALE: { includedBotTurns: null, agentSeats: null, allowOverage: false },
    ENTERPRISE: { includedBotTurns: 50000, agentSeats: 20, allowOverage: true },
  };

  const tier =
    planEnv && planEnv in tierPlans
      ? (planEnv as keyof typeof tierPlans)
      : 'BETA';
  const plan = tierPlans[tier];

  // Sensible per-tier default usage so the dev UI is not always at zero when
  // pointed at a non-BETA tier.
  const defaultTurns =
    tier === 'BETA'
      ? 248
      : tier === 'STARTER'
        ? 612
        : tier === 'GROWTH'
          ? 4200
          : tier === 'SCALE'
            ? 18_400
            : 32_000;

  const botTurnsThisMonth = turnsEnv ?? defaultTurns;

  const percentUsed =
    plan.includedBotTurns == null
      ? null
      : (botTurnsThisMonth / plan.includedBotTurns) * 100;

  return {
    plan: {
      tier,
      includedBotTurns: plan.includedBotTurns,
      agentSeats: plan.agentSeats,
      allowOverage: plan.allowOverage,
      billingCycleStart: '2026-05-01',
    },
    usage: {
      botTurnsThisMonth,
      percentUsed,
      status: percentUsed == null ? 'under' : statusFor(percentUsed),
    },
  };
}

export const mockBotUsage: BotUsage = buildMockBotUsage();
